// Config parsing — turn the tenant's `EntranceForm` definition into the
// conversational-form model, and gate the whole feature on it.
//
// Platform contract: "the widget-facing config SHALL honor `collect_via_bot`
// rather than introduce a new flag", sections are additive
// (`form_details.sections` + `metadata[0].section_id`), and legacy forms with
// neither key must keep rendering exactly as before. Absent metadata means the
// feature does not mount at all (spec: "No config, no feature") — parseForm
// returning null is the gate.
//
// Multiform (slice 4): bot-side / trigger-gated forms arrive additively under
// `BotForms` — a list of entries each carrying the same `form_details` contract
// as `EntranceForm`, so the same parser applies (BotForms delivery decided
// 2026-08-10, store tracker 6.3). Definitions only: the server remains the sole
// authority on offers (`form_offer`) and trigger evaluation. Older configs lack
// the key, and the registry then holds only the config form — every existing
// behavior is unchanged.

import { EdullaConfig } from '../../config/configType';
import { log } from '../../Utilities/logging';
import { CfField, CfFieldType, CfForm, CfOption, CfSection, FillMode } from './types';

export const IMPLICIT_SECTION_ID = 'general';

const DEFAULTS = {
    name: 'your application',
    short: 'Your Application',
    cardTitle: 'Start Your Application',
    cardSub: "I'll fill it out with you, step by step.",
    startMessage: "Great — let's begin. Tell me your name and email, and I'll fill them in for you.",
};

/**
 * `data_type` → field type. Unknown types degrade to `text` rather than dropping
 * the field, matching the contract's "unknown keys are ignored" posture.
 */
const DATA_TYPE_MAP: Record<string, CfFieldType> = {
    // Portal's legacy vocabulary (FormFieldDataTypes): Str, Num, Date,
    // DateRange, Lis, Loc. `Lis` is a picked list — its options come from
    // allowed_values, so the radio/select split below decides the control.
    str: 'text',
    num: 'number',
    lis: 'select',
    loc: 'address',
    string: 'text',
    text: 'text',
    email: 'email',
    date: 'date',
    monthyear: 'monthyear',
    daterange: 'daterange',
    number: 'number',
    int: 'number',
    float: 'number',
    phone: 'phone',
    tel: 'phone',
    zip: 'zip',
    postalcode: 'zip',
    ssn: 'ssn',
    address: 'address',
    checklist: 'checklist',
    multiselect: 'checklist',
    longtext: 'longtext',
    essay: 'longtext',
    consent: 'consent',
    group: 'group',
    select: 'select',
    radio: 'radio',
};

/**
 * Option-bearing scalar fields render as chips-friendly radios when the list is
 * short and as a picker when it is long. The production definition carries only
 * `allowed_values`, so the split is a widget-side rendering choice; the boundary
 * matches the design reference's "≤8 options render as the plain list" density
 * rule, tightened to 5 so quick-reply chips stay inside the 350px column.
 */
export const RADIO_OPTION_LIMIT = 5;

const toOptions = (allowed: unknown): CfOption[] | undefined => {
    if (!Array.isArray(allowed) || allowed.length === 0) return undefined;
    return allowed.map((entry: any) => {
        if (entry && typeof entry === 'object') {
            const value = String(entry.value ?? entry.Value ?? entry.label ?? '');
            return { value, label: String(entry.label ?? entry.Label ?? value) };
        }
        const value = String(entry);
        return { value, label: value };
    });
};

/** Types whose control is decided by how many options the field carries rather
 *  than by the authored data type — the legacy picked list, and any plain string
 *  that came with `allowed_values`. */
const OPTION_DRIVEN: Array<CfFieldType | undefined> = [undefined, 'text', 'select', 'radio'];

const resolveType = (meta: any, options?: CfOption[]): CfFieldType => {
    const raw = String(meta?.data_type ?? '').toLowerCase().replace(/[\s_-]/g, '');
    const mapped = DATA_TYPE_MAP[raw];
    if (options?.length && OPTION_DRIVEN.includes(mapped)) {
        return options.length <= RADIO_OPTION_LIMIT ? 'radio' : 'select';
    }
    if (mapped) return mapped;
    return 'text';
};

/** Types the extraction service never sees (platform "AI guardrails"). */
const NEVER_AI: CfFieldType[] = ['consent', 'group', 'longtext'];

const toPositiveInt = (raw: unknown): number | undefined => {
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
};

/**
 * One `template` subfield of a `group` field (Portal publishes
 * `{id, label, data_type, required?, allowed_values?}` — FormTemplateField in
 * the Portal's FormDefinition.cs). Parsed into a CfField shape so the group
 * control renders subfields through the existing `FieldInput` switch; the
 * synthetic section/order/collect flags never leave the parent field.
 */
const parseTemplateField = (raw: any, index: number): CfField | null => {
    if (!raw || typeof raw !== 'object' || !raw.id) return null;
    const options = toOptions(raw.allowed_values);
    const type = resolveType({ data_type: raw.data_type }, options);
    return {
        id: String(raw.id),
        label: String(raw.label ?? ''),
        type,
        optional: raw.required !== true,
        options,
        noAI: true,
        collectViaBot: false,
        sectionId: IMPLICIT_SECTION_ID,
        order: index,
    };
};

/**
 * Type-specific settings, published flat on the metadata object (Portal's
 * `BuildFieldMetadata` spreads `FormFieldSettings` into `metadata[0]`, so the
 * keys are exactly the C# `JsonProperty` names: `maxWords`, `repeat` with
 * `{min,max}`, `itemLabel`, `template`, `attestations`). Only the keys the
 * field's type uses are read; anything malformed reads as absent.
 */
const parseTypeSettings = (type: CfFieldType, meta: any): Partial<CfField> => {
    if (type === 'longtext') {
        return { maxWords: toPositiveInt(meta.maxWords) };
    }
    if (type === 'group') {
        const rawRepeat = meta.repeat;
        const min = toPositiveInt(rawRepeat?.min);
        const max = toPositiveInt(rawRepeat?.max);
        const template = (Array.isArray(meta.template) ? meta.template : [])
            .map(parseTemplateField)
            .filter((f: CfField | null): f is CfField => f !== null);
        return {
            repeat: min !== undefined || max !== undefined ? { min, max } : undefined,
            itemLabel: typeof meta.itemLabel === 'string' && meta.itemLabel.trim() ? meta.itemLabel.trim() : undefined,
            template: template.length ? template : undefined,
        };
    }
    return {};
};

/**
 * `consent` options come from `attestations` (`[{id, text}]` — the legal copy,
 * rendered verbatim), not `allowed_values`. Falls back to any
 * `allowed_values`-derived options so a consent field published the plain way
 * still renders its lines.
 */
const consentOptions = (meta: any, fallback: CfOption[] | undefined): CfOption[] | undefined => {
    const raw = Array.isArray(meta.attestations) ? meta.attestations : [];
    const options = raw
        .filter((entry: any) => entry && typeof entry === 'object' && entry.id)
        .map((entry: any) => ({ value: String(entry.id), label: String(entry.text ?? '') }));
    return options.length ? options : fallback;
};

type ParseContext = {
    fields: CfField[];
    order: { n: number };
    parentId?: string;
    parentValue?: string | string[];
};

/** Coerce a published conditional gate (`show_on_value` /
 *  `parent_conditional_value`) to the widget shape: a string, or an array when
 *  the field declares several trigger values. */
const toGate = (raw: unknown): string | string[] | undefined => {
    if (raw === undefined || raw === null) return undefined;
    return Array.isArray(raw) ? raw.map(String) : String(raw);
};

const parseField = (raw: any, ctx: ParseContext): void => {
    const meta = raw?.metadata?.[0] ?? {};
    const fieldId = meta.field_id;
    // A field with no id cannot participate in extraction, receipts, or save —
    // all three key on field_id. Skip it rather than invent one.
    if (!fieldId) return;

    const options = toOptions(meta.allowed_values);
    const type = resolveType(meta, options);

    // Conditional linkage has two sources (platform contract, tracker 6.26b):
    // an in-form child arrives nested (the enclosing `Children` group supplies
    // the gate via ctx), while a field whose parent lives in ANOTHER form is
    // published as a top-level property whose metadata carries the linkage
    // (`parent_field_id`, `parent_conditional_value`). Nesting wins when both
    // are present — they name the same link, and the group is the shape older
    // configs carry.
    const parentId =
        ctx.parentId ?? (meta.parent_field_id ? String(meta.parent_field_id) : undefined);
    const parentValue =
        ctx.parentId !== undefined ? ctx.parentValue : toGate(meta.parent_conditional_value);

    const field: CfField = {
        id: String(fieldId),
        label: String(raw.title ?? ''),
        type,
        optional: meta.required !== true,
        options: type === 'consent' ? consentOptions(meta, options) : options,
        ...parseTypeSettings(type, meta),
        validation: meta.validation ? String(meta.validation) : undefined,
        help: raw.description ? String(raw.description) : undefined,
        sensitive: meta.sensitive === true,
        noAI: meta.no_ai === true || NEVER_AI.includes(type),
        collectViaBot: meta.collect_via_bot === true,
        sectionId: meta.section_id ? String(meta.section_id) : IMPLICIT_SECTION_ID,
        order: typeof meta.Order === 'number' ? meta.Order : ctx.order.n,
        parentId,
        parentValue,
    };
    ctx.order.n += 1;
    ctx.fields.push(field);

    // Conditional children — new style (`Children: [{show_on_value, fields}]`)
    // and the legacy pair (`children` + `children_show_on_value`). Nesting is
    // unlimited; each level inherits its parent's id as the gate. Children
    // flatten into the form's field list right after their parent (the order
    // counter runs depth-first), each carrying the linkage the visibility
    // resolver reads.
    const groups: Array<{ show_on_value: any; fields: any }> = Array.isArray(raw.Children)
        ? raw.Children
        : raw.children
            ? [{ show_on_value: raw.children_show_on_value ?? '', fields: raw.children }]
            : [];

    groups.forEach((group) => {
        const childFields = group?.fields ?? {};
        Object.values(childFields).forEach((child) =>
            parseField(child, {
                ...ctx,
                parentId: field.id,
                parentValue: toGate(group.show_on_value),
            })
        );
    });
};

const parseSections = (formDetails: any): CfSection[] => {
    const raw = formDetails?.sections;
    if (!Array.isArray(raw) || raw.length === 0) {
        return [{ id: IMPLICIT_SECTION_ID, title: 'General', order: 0 }];
    }
    return raw
        .map((section: any, index: number) => ({
            id: String(section.section_id ?? IMPLICIT_SECTION_ID),
            title: String(section.title ?? 'General'),
            blurb: section.blurb ? String(section.blurb) : undefined,
            order: typeof section.order === 'number' ? section.order : index,
        }))
        .sort((a, b) => a.order - b.order);
};

const resolveFillMode = (value: unknown): FillMode => (value === 'suggested' ? 'suggested' : 'auto');

/**
 * One authored display-copy string (platform contract, store tracker 6.14 —
 * widget half): only a non-empty string counts. Anything else — an absent
 * `display` key, a non-object value, numbers, whitespace-only strings — falls
 * through to today's derived copy, so configs published before the Portal
 * emits `display` parse byte-identically.
 */
const toAuthoredCopy = (raw: unknown): string | undefined => {
    if (typeof raw !== 'string') return undefined;
    const trimmed = raw.trim();
    return trimmed === '' ? undefined : trimmed;
};

/**
 * Parse one `form_details` payload (the shared contract of `EntranceForm` and
 * each `BotForms` entry) into a `CfForm`, or null when it is not a
 * conversational form. `variant` only decides card copy: the entrance form
 * keeps its historical defaults byte-for-byte; a bot form derives its copy from
 * the authored title, because "Start Your Application" on a recruiting form
 * would be a lie. `browsable` is the caller's reading of the entry-level flag
 * (store tracker 6.30): false marks an ambient form that is never
 * visitor-startable.
 */
const parseFormDetails = (
    formDetails: any,
    fillMode: FillMode,
    variant: 'entrance' | 'bot',
    browsable: boolean
): CfForm | null => {
    const properties = formDetails?.properties;
    if (!properties || typeof properties !== 'object') return null;

    const objectId = formDetails.object_id;
    // The save and dismiss contracts are both keyed by object_id. Without it the
    // feature could collect answers it can never persist or suppress.
    if (!objectId) return null;

    const fields: CfField[] = [];
    const ctx: ParseContext = { fields, order: { n: 0 } };
    Object.values(properties).forEach((raw) => parseField(raw, ctx));

    // The gate itself: conversational collection is opt-in per field, so a form
    // with no `collect_via_bot` field is not a conversational form.
    if (!fields.some((f) => f.collectViaBot)) return null;

    const sections = parseSections(formDetails);
    const known = new Set(sections.map((s) => s.id));
    fields.forEach((f) => {
        if (!known.has(f.sectionId)) f.sectionId = sections[0].id;
    });

    // Visitor-facing copy — THE derivation point (platform contract, store
    // tracker 6.14 — widget half). Authored `display.title` is the form's
    // human-facing name and `display.subtitle` the start card's one-line sub;
    // every visitor surface (start card, pinned header, menu and Home rows,
    // overlay title, and the acknowledgement/start message templates) consumes
    // the CfForm copy fields derived here, so replacing the internal name in
    // this one spot replaces it everywhere the visitor can see. Absent copy
    // keeps the historical derivation byte-for-byte: the internal object title
    // carries what it can and the rest falls back to neutral defaults.
    const display = (formDetails as any).display;
    const displayTitle = toAuthoredCopy(display?.title);
    const displaySubtitle = toAuthoredCopy(display?.subtitle);
    const title = formDetails.title && formDetails.title !== 'FormDetails' ? String(formDetails.title) : null;
    // The visitor-facing name: authored copy first, then the internal title.
    const visitorName = displayTitle ?? title;
    const named = variant === 'bot' && visitorName !== null;
    return {
        objectId: String(objectId),
        displayTitle,
        displaySubtitle,
        name: visitorName ?? DEFAULTS.name,
        short: visitorName ?? DEFAULTS.short,
        // An authored title IS the card title — the author writes exactly what
        // the visitor reads; only the derived fallback prepends "Start".
        cardTitle: displayTitle ?? (named ? `Start ${title}` : DEFAULTS.cardTitle),
        cardSub: displaySubtitle ?? DEFAULTS.cardSub,
        startMessage: named
            ? `Great — let's start the ${visitorName}. Tell me a bit and I'll fill it in as we go.`
            : DEFAULTS.startMessage,
        sections,
        fields: fields.sort((a, b) => a.order - b.order),
        fillMode,
        browsable,
    };
};

/**
 * Build the conversational form from tenant config, or return null when the
 * tenant has not enabled it. Null is the feature gate — every surface and every
 * network call in this feature hangs off a non-null form.
 */
export const parseConversationalForm = (config: EdullaConfig | null | undefined): CfForm | null =>
    parseFormDetails(
        (config as any)?.EntranceForm?.form_details,
        // Flat tenant-config key (Portal `forms-config-publish`); absent = auto
        // in every consumer. Suggested mode stays dormant until Portal publishes
        // it and Bot.API stops persisting at extraction.
        resolveFillMode((config as any)?.fill_mode),
        'entrance',
        // The tenant's own form is by definition a visitor task.
        true
    );

/**
 * The multiform registry: every form this widget holds a definition for, keyed
 * by `objectId`. The config form (when present) is `primary` and always first;
 * `BotForms` entries follow in published order.
 */
export type CfRegistry = {
    /** The tenant's `EntranceForm` — the pre-start default for open fields and
     *  implicit first-fill activation. Null for a BotForms-only tenant. */
    primary: CfForm | null;
    /** Primary first, then bot forms in published order. */
    forms: CfForm[];
    byId: Map<string, CfForm>;
    /**
     * Every field across every registered form, keyed by field id (unique
     * registry-wide — colliding bot forms are dropped whole). The conditional
     * visibility resolver walks parent chains through this index, because a
     * parent MAY live in a different form (platform contract, tracker 6.26b).
     */
    fieldsById: Map<string, CfField>;
};

/**
 * Parse the whole registry, or null when the tenant has no conversational form
 * at all (the feature gate, unchanged). Design D1 keeps one shared values map
 * keyed by field id, so field ids must be unique across the registry: a bot
 * form whose ids collide with an already-accepted form is dropped whole (and
 * logged) rather than allowed to leak values between forms.
 */
export const parseFormsRegistry = (config: EdullaConfig | null | undefined): CfRegistry | null => {
    const fillMode = resolveFillMode((config as any)?.fill_mode);
    const primary = parseConversationalForm(config);

    const forms: CfForm[] = primary ? [primary] : [];
    const formIds = new Set(forms.map((f) => f.objectId));
    const fieldIds = new Set(forms.flatMap((f) => f.fields.map((field) => field.id)));

    const rawBotForms = (config as any)?.BotForms;
    (Array.isArray(rawBotForms) ? rawBotForms : []).forEach((entry: any) => {
        // Entry-level flag, a sibling of form_details (store tracker 6.30):
        // only an explicit `browsable: false` marks the ambient, never
        // visitor-startable form — absent means browsable, so configs
        // published before the flag existed parse exactly as before.
        const parsed = parseFormDetails(entry?.form_details, fillMode, 'bot', entry?.browsable !== false);
        if (!parsed || formIds.has(parsed.objectId)) return;
        if (parsed.fields.some((f) => fieldIds.has(f.id))) {
            log(
                'edulla-chat',
                `BotForms entry ${parsed.objectId} dropped: field ids collide with another registered form`,
                'visitor',
                { sessionId: '' }
            );
            return;
        }
        formIds.add(parsed.objectId);
        parsed.fields.forEach((f) => fieldIds.add(f.id));
        forms.push(parsed);
    });

    if (!forms.length) return null;
    return {
        primary,
        forms,
        byId: new Map(forms.map((f) => [f.objectId, f])),
        fieldsById: new Map(forms.flatMap((f) => f.fields.map((field) => [field.id, field] as const))),
    };
};

export const fieldById = (form: CfForm | null, id: string): CfField | undefined =>
    form?.fields.find((f) => f.id === id);
